import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { AuthContext } from '../context/AuthContext';
import API_BASE_URL from '../config/api';
import './Access.css';

const RoleForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { hasPermission } = useContext(AuthContext);

  // Someone with only roles.view can open a role, but not change it
  const canSave = isEdit ? hasPermission('roles.update') : hasPermission('roles.create');

  const [modules, setModules] = useState([]);
  const [form, setForm] = useState({ name: '', description: '', isActive: true });
  const [selected, setSelected] = useState([]);
  const [isSystem, setIsSystem] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError('');

        const catalogue = await axios.get(`${API_BASE_URL}/admin/roles/permissions`);
        setModules(catalogue.data?.data?.modules || []);

        if (isEdit) {
          const response = await axios.get(`${API_BASE_URL}/admin/roles/${id}`);
          const role = response.data?.data;
          if (role) {
            setForm({
              name: role.name || '',
              description: role.description || '',
              isActive: role.isActive !== false
            });
            setSelected(role.permissions || []);
            setIsSystem(Boolean(role.isSystem));
          }
        }
      } catch (err) {
        setError(err?.response?.data?.message || 'Failed to load role');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, isEdit]);

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const allPermissions = useMemo(
    () => modules.flatMap((mod) => mod.actions.map((a) => a.permission)),
    [modules]
  );

  const togglePermission = (permission) => {
    setSelected((prev) =>
      prev.includes(permission) ? prev.filter((p) => p !== permission) : [...prev, permission]
    );
  };

  const toggleModule = (mod) => {
    const modPerms = mod.actions.map((a) => a.permission);
    const allOn = modPerms.every((p) => selectedSet.has(p));
    setSelected((prev) =>
      allOn
        ? prev.filter((p) => !modPerms.includes(p))
        : [...new Set([...prev, ...modPerms])]
    );
  };

  const toggleAll = () => {
    setSelected(selected.length === allPermissions.length ? [] : [...allPermissions]);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!form.name.trim()) {
      setError('Role name is required');
      return;
    }
    if (selected.length === 0) {
      setError('Select at least one permission for this role');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description,
        isActive: form.isActive,
        permissions: selected
      };

      if (isEdit) {
        await axios.put(`${API_BASE_URL}/admin/roles/${id}`, payload);
      } else {
        await axios.post(`${API_BASE_URL}/admin/roles`, payload);
      }
      navigate('/sy-admin/roles');
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save role');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="container">
          <div className="access-form-card">
            <div className="skeleton skeleton-text" style={{ width: 240, height: 24, marginBottom: 16 }} />
            <div className="skeleton skeleton-text" style={{ width: '100%', height: 120 }} />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container">
        <div className="access-header">
          <div>
            <h1>{isEdit ? 'Edit Role' : 'Add New Role'}</h1>
            <p>Tick everything this role should be able to do.</p>
          </div>
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/sy-admin/roles')}>
            Back to Roles
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        {isSystem && (
          <div className="success-message" style={{ background: '#f1f5f9', color: '#475569' }}>
            This is a default role. You can change its permissions, but it cannot be deleted.
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="access-form-card">
            <h2>Role details</h2>
            <div className="access-form-grid">
              <div className="form-group">
                <label>Role Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., Front Desk"
                  disabled={!canSave}
                  required
                />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  value={form.isActive ? 'active' : 'inactive'}
                  onChange={(e) => setForm({ ...form, isActive: e.target.value === 'active' })}
                  disabled={!canSave}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
                placeholder="What is this role for?"
                disabled={!canSave}
              />
            </div>
            <p className="access-hint">
              Deactivating a role immediately removes access for every employee assigned to it.
            </p>
          </div>

          <div className="access-form-card">
            <div className="permission-toolbar">
              <h2 style={{ margin: 0 }}>Permissions</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <span className="permission-count">
                  {selected.length} of {allPermissions.length} selected
                </span>
                {canSave && (
                  <button type="button" className="btn btn-secondary" onClick={toggleAll}>
                    {selected.length === allPermissions.length ? 'Clear all' : 'Select all'}
                  </button>
                )}
              </div>
            </div>

            <div className="permission-modules">
              {modules.map((mod) => {
                const modPerms = mod.actions.map((a) => a.permission);
                const allOn = modPerms.every((p) => selectedSet.has(p));
                const someOn = !allOn && modPerms.some((p) => selectedSet.has(p));

                return (
                  <div className="permission-module" key={mod.key}>
                    <div className="permission-module-header">
                      <span className="permission-module-title">{mod.label}</span>
                      <label className="permission-checkbox is-all">
                        <input
                          type="checkbox"
                          checked={allOn}
                          ref={(el) => {
                            if (el) el.indeterminate = someOn;
                          }}
                          onChange={() => toggleModule(mod)}
                          disabled={!canSave}
                        />
                        <span>Select all</span>
                      </label>
                    </div>
                    <div className="permission-actions">
                      {mod.actions.map((action) => (
                        <label className="permission-checkbox" key={action.permission}>
                          <input
                            type="checkbox"
                            checked={selectedSet.has(action.permission)}
                            onChange={() => togglePermission(action.permission)}
                            disabled={!canSave}
                          />
                          <span>{action.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="access-form-footer">
            <button type="button" className="btn btn-secondary" onClick={() => navigate('/sy-admin/roles')}>
              Cancel
            </button>
            {canSave && (
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving…' : isEdit ? 'Update Role' : 'Create Role'}
              </button>
            )}
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default RoleForm;
